import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon, Bars3Icon, CheckCircleIcon, BoltIcon } from '@heroicons/react/24/outline';
import {
  DEFAULT_OPERATIONAL_COLORS,
  useAppTheme,
  type AppTheme,
  type OperationalColorKey
} from '@/app/providers/theme-provider';
import { useLanguage, setLanguage, languages, t } from '@/app/utils/i18n';
import { toast } from 'sonner';

const LANGUAGE_OPTIONS = [
  { code: 'pt', label: 'Português', description: 'Operação em português para equipas técnicas e produtores.' },
  { code: 'en', label: 'English', description: 'English interface for international technical teams.' },
  { code: 'fr', label: 'Français', description: 'Interface en français pour équipes scientifiques et opérationnelles.' },
  { code: 'es', label: 'Español', description: 'Interfaz en español para operación y validación técnica.' },
  { code: 'de', label: 'Deutsch', description: 'Deutsche Oberfläche für technische Betriebsumgebungen.' }
];

const THEME_OPTIONS: Array<{ id: AppTheme; name: string; description: string }> = [
  { id: 'dark-industrial', name: 'Dark', description: 'Operação noturna, contraste alto e foco em alarmes.' },
  { id: 'light', name: 'Light', description: 'Ambientes de escritório, leitura rápida e menos fadiga em luz forte.' }
];

const COLOR_GROUPS: Array<{
  title: string;
  items: Array<{ key: OperationalColorKey; label: string }>;
}> = [
  {
    title: 'Severidade',
    items: [
      { key: 'severityCritical', label: 'Crítica' },
      { key: 'severityHigh', label: 'Alta' },
      { key: 'severityMedium', label: 'Média' },
      { key: 'severityLow', label: 'Baixa' }
    ]
  },
  {
    title: 'Estados',
    items: [
      { key: 'statusOnline', label: 'Online / OK' },
      { key: 'statusOffline', label: 'Offline' },
      { key: 'statusWarning', label: 'Atenção' },
      { key: 'statusInfo', label: 'Informativo' }
    ]
  },
  {
    title: 'Validação',
    items: [
      { key: 'validationPending', label: 'Pendente' },
      { key: 'validationConfirmed', label: 'Confirmado' },
      { key: 'validationRejected', label: 'Falso positivo' },
      { key: 'validationInconclusive', label: 'Inconclusivo' }
    ]
  }
];

// Menu items - sincronizados com vision-alerts.tsx
type VisionMainView = 'operations' | 'clinical' | 'sensors' | 'growth' | 'live' | 'analytics';

interface MenuItemDisplay {
  id: VisionMainView;
  label: string;
}

const MENU_ITEMS_DISPLAY: MenuItemDisplay[] = [
  { id: 'operations', label: 'Operações' },
  { id: 'clinical', label: 'Clínicos' },
  { id: 'sensors', label: 'Sensores' },
  { id: 'growth', label: 'Crescimento' },
  { id: 'live', label: 'Live' },
  { id: 'analytics', label: 'Análises' }
];

const DEFAULT_MENU_ORDER: VisionMainView[] = ['operations', 'clinical', 'sensors', 'growth', 'live', 'analytics'];
const MENU_ORDER_STORAGE_KEY = 'aql_vision_sidebar_menu_order';
const MENU_HIDDEN_STORAGE_KEY = 'aql_vision_sidebar_menu_hidden';
const MENU_ORDER_EVENT = 'aql-vision-menu-order-changed';

export function PersonalizationSettings() {
  const { theme, setTheme, operationalColors, setOperationalColor, resetOperationalColors } = useAppTheme();
  const language = useLanguage();
  const [menuOrder, setMenuOrder] = useState<VisionMainView[]>(DEFAULT_MENU_ORDER);
  const [hiddenItems, setHiddenItems] = useState<VisionMainView[]>([]);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  // Load menu preferences from localStorage (sincronizadas com vision-alerts)
  useEffect(() => {
    try {
      const savedOrder = localStorage.getItem(MENU_ORDER_STORAGE_KEY);
      if (savedOrder) {
        const parsed = JSON.parse(savedOrder);
        if (Array.isArray(parsed)) {
          setMenuOrder(parsed);
        }
      }
      
      const savedHidden = localStorage.getItem(MENU_HIDDEN_STORAGE_KEY);
      if (savedHidden) {
        const parsed = JSON.parse(savedHidden);
        if (Array.isArray(parsed)) {
          setHiddenItems(parsed);
        }
      }
    } catch {
      // Valores por defeito
    }
  }, []);

  // Save menu order to localStorage
  const saveMenuOrder = (newOrder: VisionMainView[]) => {
    localStorage.setItem(MENU_ORDER_STORAGE_KEY, JSON.stringify(newOrder));
    setMenuOrder(newOrder);
    window.dispatchEvent(new Event(MENU_ORDER_EVENT));
    toast.success('Ordem do menu atualizada');
  };

  // Save hidden items to localStorage
  const saveHiddenItems = (hidden: VisionMainView[]) => {
    localStorage.setItem(MENU_HIDDEN_STORAGE_KEY, JSON.stringify(hidden));
    setHiddenItems(hidden);
    window.dispatchEvent(new Event(MENU_ORDER_EVENT));
    toast.success('Menu atualizado');
  };

  const toggleMenuItemVisibility = (id: VisionMainView) => {
    if (hiddenItems.includes(id)) {
      saveHiddenItems(hiddenItems.filter(item => item !== id));
    } else {
      saveHiddenItems([...hiddenItems, id]);
    }
  };

  const moveMenuItem = (fromIndex: number, toIndex: number) => {
    const items = [...menuOrder];
    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);
    saveMenuOrder(items);
  };

  const visibleMenuItems = menuOrder.filter(item => !hiddenItems.includes(item));

  return (
    <div className="space-y-6">
      {/* TEMA DA INTERFACE */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-border dark:bg-card/50 dark:shadow-none">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 dark:text-muted-foreground">Personalização</p>
          <h2 className="mt-2 text-2xl font-bold text-foreground">Tema da interface</h2>
          <p className="mt-2 text-sm font-medium text-slate-600 dark:text-muted-foreground">
            Escolhe o ambiente visual que melhor se adapta à operação. A alteração é aplicada de imediato em toda a plataforma.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {THEME_OPTIONS.map((themeOption) => (
            <button
              key={themeOption.id}
              onClick={() => {
                setTheme(themeOption.id);
                toast.success(`Tema alterado para ${themeOption.name}`);
              }}
              className={`group relative overflow-hidden rounded-2xl border-2 p-4 transition-all ${
                theme === themeOption.id
                  ? 'border-sky-400 bg-sky-50 shadow-[0_18px_42px_-32px_rgba(2,132,199,0.65)] ring-1 ring-sky-200 dark:border-primary dark:bg-primary/10 dark:ring-primary/20'
                  : 'border-slate-200 bg-white hover:border-sky-300 hover:bg-slate-50 dark:border-border/50 dark:bg-card/30 dark:hover:border-primary/50 dark:hover:bg-card/55'
              }`}
            >
              {/* Theme preview */}
              <div className="relative mb-4 h-24 rounded-xl">
                {themeOption.id === 'dark-industrial' && (
                  <div className="flex h-full items-center justify-center gap-2 rounded-lg bg-slate-950 p-3">
                    <div className="h-8 w-12 rounded bg-cyan-500"></div>
                    <div className="space-y-1">
                      <div className="h-2 w-16 rounded bg-slate-700"></div>
                      <div className="h-2 w-12 rounded bg-slate-700"></div>
                    </div>
                  </div>
                )}
                {themeOption.id === 'light' && (
                  <div className="flex h-full items-center justify-center gap-2 rounded-lg bg-white p-3">
                    <div className="h-8 w-12 rounded bg-blue-600"></div>
                    <div className="space-y-1">
                      <div className="h-2 w-16 rounded bg-gray-300"></div>
                      <div className="h-2 w-12 rounded bg-gray-300"></div>
                    </div>
                  </div>
                )}
              </div>

              <h3 className="font-semibold text-foreground transition-colors dark:text-slate-100 dark:group-hover:text-white">{themeOption.name}</h3>
              <p className="mt-1 text-xs font-medium text-slate-600 transition-colors dark:text-slate-300 dark:group-hover:text-slate-100">{themeOption.description}</p>

              {theme === themeOption.id && (
                <div className="absolute top-2 right-2">
                  <CheckCircleIcon className="h-5 w-5 text-sky-600 dark:text-primary" />
                </div>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* CORES OPERACIONAIS */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-border dark:bg-card/50 dark:shadow-none">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 dark:text-muted-foreground">Cores operacionais</p>
            <h2 className="mt-2 text-2xl font-bold text-foreground">Estados, severidade e validação</h2>
            <p className="mt-2 max-w-3xl text-sm font-medium text-slate-600 dark:text-muted-foreground">
              Configura as cores que comunicam risco, estado de dispositivos e decisão humana. Cada tema mantém a sua própria paleta.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              resetOperationalColors(theme);
              toast.success('Cores operacionais repostas para o tema atual');
            }}
            className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 transition-colors hover:border-sky-300 hover:bg-slate-50 dark:border-border/60 dark:text-muted-foreground dark:hover:border-primary/40 dark:hover:bg-muted/40"
          >
            Repor tema atual
          </button>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          {THEME_OPTIONS.map((themeOption) => {
            const palette = operationalColors[themeOption.id] ?? DEFAULT_OPERATIONAL_COLORS[themeOption.id];
            const isActiveTheme = theme === themeOption.id;

            return (
              <div
                key={themeOption.id}
                className={`rounded-xl border p-4 ${
                  isActiveTheme
                    ? 'border-sky-300 bg-sky-50/70 ring-1 ring-sky-100 dark:border-primary/40 dark:bg-primary/10 dark:ring-primary/20'
                    : 'border-slate-200 bg-slate-50 dark:border-border/50 dark:bg-muted/20'
                }`}
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold text-foreground">{themeOption.name}</h3>
                    <p className="text-xs font-medium text-slate-600 dark:text-muted-foreground">
                      {isActiveTheme ? 'Tema ativo' : 'Paleta guardada'}
                    </p>
                  </div>
                  {isActiveTheme && <CheckCircleIcon className="h-5 w-5 shrink-0 text-sky-600 dark:text-primary" />}
                </div>

                <div className="space-y-4">
                  {COLOR_GROUPS.map((group) => (
                    <div key={`${themeOption.id}-${group.title}`}>
                      <p className="mb-2 text-[0.66rem] font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-muted-foreground">
                        {group.title}
                      </p>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-2 2xl:grid-cols-4">
                        {group.items.map((item) => {
                          const inputId = `operational-color-${themeOption.id}-${item.key}`;
                          return (
                            <label
                              key={item.key}
                              htmlFor={inputId}
                              className="group flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 transition-colors hover:border-sky-300 hover:bg-slate-50 dark:border-border/50 dark:bg-card/30 dark:hover:border-primary/40 dark:hover:bg-card/50"
                              title={`Alterar cor: ${item.label}`}
                            >
                              <span
                                className="h-4 w-4 shrink-0 rounded-full border border-black/10 shadow-sm ring-2 ring-white dark:border-white/20 dark:ring-slate-950/50"
                                style={{ backgroundColor: palette[item.key] }}
                              />
                              <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
                                {item.label}
                              </span>
                              <input
                                id={inputId}
                                type="color"
                                value={palette[item.key]}
                                onChange={(event) => setOperationalColor(themeOption.id, item.key, event.target.value)}
                                className="h-5 w-5 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0 opacity-0 group-hover:opacity-100 focus:opacity-100"
                                aria-label={`Cor de ${item.label} no tema ${themeOption.name}`}
                              />
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* IDIOMA DA INTERFACE */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-border dark:bg-card/50 dark:shadow-none">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 dark:text-muted-foreground">Idioma</p>
          <h2 className="mt-2 text-2xl font-bold text-foreground">Idioma da interface</h2>
          <p className="mt-2 text-sm font-medium text-slate-600 dark:text-muted-foreground">
            Define o idioma usado nos painéis, validações, alertas e controlos operacionais do AQL Vision.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-5">
          {LANGUAGE_OPTIONS.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code as any);
                toast.success(`Idioma alterado para ${lang.label}`);
              }}
              className={`group relative rounded-2xl border-2 p-4 transition-all ${
                language === lang.code
                  ? 'border-sky-400 bg-sky-50 shadow-[0_18px_42px_-34px_rgba(2,132,199,0.6)] ring-1 ring-sky-200 dark:border-primary dark:bg-primary/10 dark:ring-primary/20'
                  : 'border-slate-200 bg-white hover:border-sky-300 hover:bg-slate-50 dark:border-border/50 dark:bg-card/30 dark:hover:border-primary/50'
              }`}
            >
              <div className="mb-2 flex h-12 items-center justify-center rounded-lg bg-slate-100 font-bold text-sky-600 dark:bg-muted/50 dark:text-primary">
                {lang.code.toUpperCase()}
              </div>
              <h3 className="font-semibold text-foreground">{lang.label}</h3>
              <p className="mt-2 text-xs font-medium text-slate-600 dark:text-muted-foreground">{lang.description}</p>

              {language === lang.code && (
                <div className="absolute top-2 right-2">
                  <CheckCircleIcon className="h-5 w-5 text-sky-600 dark:text-primary" />
                </div>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* ORDEM DO MENU LATERAL */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-border dark:bg-card/50 dark:shadow-none">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 dark:text-muted-foreground">Menu Lateral</p>
          <h2 className="mt-2 text-2xl font-bold text-foreground">Ordem da navegação</h2>
          <p className="mt-2 text-sm font-medium text-slate-600 dark:text-muted-foreground">
            Arrasta para definir a ordem e usa o olho para mostrar ou esconder opções. Configuração fica fixa e não entra nesta lista.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          {/* Drag and drop list */}
          <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-border/50 dark:bg-muted/20">
            {menuOrder.map((itemId, index) => {
              const item = MENU_ITEMS_DISPLAY.find(m => m.id === itemId);
              if (!item) return null;
              return (
                <div
                  key={itemId}
                  draggable
                  onDragStart={() => setDraggedItem(itemId)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (draggedItem && draggedItem !== itemId) {
                      const draggedIndex = menuOrder.findIndex(i => i === draggedItem as VisionMainView);
                      moveMenuItem(draggedIndex, index);
                    }
                    setDraggedItem(null);
                  }}
                  className={`group flex cursor-grab items-center gap-3 rounded-lg border px-3 py-3 transition-all active:cursor-grabbing ${
                    draggedItem === itemId
                      ? 'border-sky-400 bg-sky-50 opacity-70 dark:border-primary/50 dark:bg-primary/10'
                      : 'border-slate-200 bg-white hover:border-sky-300 hover:bg-sky-50 dark:border-border/30 dark:bg-card/30 dark:hover:border-primary/30 dark:hover:bg-card/50'
                  }`}
                >
                  <Bars3Icon className="h-5 w-5 text-slate-500 group-hover:text-sky-600 dark:text-muted-foreground/60 dark:group-hover:text-primary/70" />
                  <span className="flex-1 font-medium text-foreground">{item.label}</span>
                  <button
                    onClick={() => toggleMenuItemVisibility(itemId)}
                    className="rounded-lg border border-transparent p-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-muted/50"
                    title={hiddenItems.includes(itemId) ? 'Mostrar' : 'Esconder'}
                  >
                    {hiddenItems.includes(itemId) ? (
                      <EyeSlashIcon className="h-4 w-4 text-slate-400 dark:text-muted-foreground/50" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-slate-600 dark:text-muted-foreground/70" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Preview */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-border/50 dark:bg-muted/20">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 mb-3 dark:text-muted-foreground">
              Preview do menu
            </p>
            <div className="space-y-1">
              {visibleMenuItems.map((itemId) => {
                const item = MENU_ITEMS_DISPLAY.find(m => m.id === itemId);
                if (!item) return null;
                return (
                  <div
                    key={itemId}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      menuOrder.indexOf(itemId) === 0
                        ? 'bg-sky-500 text-slate-950 dark:bg-primary dark:text-primary-foreground'
                        : 'bg-white text-slate-700 dark:bg-muted/50 dark:text-foreground'
                    }`}
                  >
                    {item.label}
                  </div>
                );
              })}
              <div className="border-t border-border/30 mt-2 pt-2">
                <div className="text-xs font-semibold text-slate-500 flex items-center gap-2 dark:text-muted-foreground/60">
                  <BoltIcon className="h-3 w-3 text-sky-600 dark:text-current" />
                  Configuração
                </div>
              </div>
            </div>
            {visibleMenuItems.length === 0 && (
              <p className="text-xs text-muted-foreground italic">Nenhum item visível</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
